import {
  ActionSchema,
  DynamicBooleanSchema,
  DynamicStringSchema,
  DynamicValueSchema,
} from '@a2ui/web_core/v0_9';
import { createComponentImplementation } from '@a2ui/react/v0_9';
import { defineA2uiComponentSchema } from '../../a2ui-schema';
import {
  EditQuote,
  type EditQuoteChange,
  type EditQuoteSaveResult,
  type EditQuoteSaveError,
} from './EditQuote';

interface ResolvedProps {
  readonly accountName?: string;
  readonly quoteNumber?: string;
  readonly currentPremium?: string;
  readonly currentLimit?: string;
  readonly currentDeductible?: string;
  readonly changes?: EditQuoteChange[];
  readonly previewedPremium?: string;
  // Populated reactively by A2UISurfaceList's result-injection branch at
  // `/results/<this component's id>`, never sent by the agent up front —
  // either a real EditQuoteSaveResult (success) or an EditQuoteSaveError
  // (failure), written to the same path.
  readonly result?: EditQuoteSaveResult | EditQuoteSaveError;
  readonly pending?: boolean;
  readonly onSave?: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const schema = defineA2uiComponentSchema({
  accountName: DynamicStringSchema,
  quoteNumber: DynamicStringSchema,
  currentPremium: DynamicStringSchema,
  currentLimit: DynamicStringSchema,
  currentDeductible: DynamicStringSchema,
  // A literal array of {label, from, to} rows, fixed at preview time — same
  // "no dynamic binding needed" reasoning as QuoteOption's generatePayload.
  changes: DynamicValueSchema,
  previewedPremium: DynamicStringSchema,
  result: DynamicValueSchema,
  pending: DynamicBooleanSchema,
  onSave: ActionSchema,
}) as any;

export const EditQuoteImpl = createComponentImplementation(
  { name: 'EditQuote', schema },
  ({
    props,
  }: {
    props: ResolvedProps;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    buildChild: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    context: any;
  }) => (
    <EditQuote
      accountName={props.accountName ?? ''}
      quoteNumber={props.quoteNumber}
      currentPremium={props.currentPremium}
      currentLimit={props.currentLimit}
      currentDeductible={props.currentDeductible}
      changes={
        Array.isArray(props.changes)
          ? (props.changes as EditQuoteChange[])
          : undefined
      }
      previewedPremium={props.previewedPremium}
      result={
        props.result as EditQuoteSaveResult | EditQuoteSaveError | undefined
      }
      pending={props.pending === true}
      onSave={props.onSave}
    />
  )
);
